import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check, ChevronDown, CircleAlert, Loader2, MessageSquare, Send, X,
  Image as ImageIcon, Video, Sparkles, RefreshCw, Shield, PackageCheck,
  Paperclip,
} from 'lucide-react';

const STAGE_LABELS = {
  idle: '等待开始',
  awaiting_script: '导入剧本',
  plan_review: '确认计划',
  asset_confirmation: '检查资产',
  shot_confirmation: '确认片段',
  keyframe_generation: '生成首帧',
  video_submission: '提交视频',
  video_processing: '视频生成中',
  video_review: '验收候选',
  revision_review: '确认返工',
  retry_review: '失败处理',
  delivery_ready: '素材交付',
};

const ACTION_LABELS = {
  start_production: '建图（拆解上画布）',
  create_storyboard: '创建画布生产链',
  generate_assets: '生成资产参考图',
  generate_keyframes: '生成片段首帧',
  submit_videos: '生成视频',
  retry_generation: '采用局部返工方案',
};

const ASSET_TYPE_LABELS = {
  character: '角色',
  role: '角色',
  person: '角色',
  scene: '场景',
  location: '场景',
  prop: '道具',
  object: '道具',
  image: '图片',
  video: '视频',
  fx: '特效',
  fx_move: '招式特效',
};

const toArray = value => {
  if (!value) return [];
  return Array.isArray(value) ? value.filter(Boolean) : [value];
};

const displayValue = value => {
  if (value == null || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') return value.name || value.label || value.id || JSON.stringify(value);
  return String(value);
};

const actionStatusText = (status) => {
  if (status === 'failed') return '失败';
  if (status === 'blocked') return '已阻断';
  if (status === 'confirmed' || status === 'running' || status === 'processing') return status === 'confirmed' ? '已确认' : '处理中';
  return '';
};

const nodeTypeLabel = (node) => {
  if (node.data?.agent_role === 'asset') return '资产';
  if (node.type === 'imageNode') return '图片';
  if (node.type === 'audioNode') return '音频';
  if (node.type === 'textNode') return '文本';
  if (['clip', 'video_clip'].includes(node.data?.agent_role) || node.type === 'videoNode') return '视频';
  if (node.data?.agent_role === 'shot_task') return '分镜';
  return '节点';
};

const nodeMentionLabel = (node) => {
  const asset = node.data?.asset || {};
  const explicit = asset.name || node.data?.label || node.data?.title || node.data?.shot_id;
  if (explicit) return explicit;
  // 没名字别露 UUID，给个可辨别的短标识（图片·e267）
  const typeName = node.type === 'imageNode' ? '图片' : node.type === 'audioNode' ? '音频' : node.type === 'videoNode' ? '视频' : node.type === 'textNode' ? '文本' : '节点';
  return `${typeName}·${String(node.id || '').slice(0, 4)}`;
};

const isTextLikeFile = (file) => {
  const name = String(file?.name || '').toLowerCase();
  return Boolean(
    file?.type?.startsWith('text/')
    || ['.txt', '.md', '.markdown', '.csv', '.srt', '.json'].some(ext => name.endsWith(ext))
  );
};

// eslint-disable-next-line no-unused-vars
function AssetGapCard({ gap }) {
  const assetType = ASSET_TYPE_LABELS[gap.asset_type] || gap.asset_type || '素材';
  const assetName = gap.asset_name || gap.name || gap.label || '未命名素材';
  const linkedShots = toArray(gap.linked_shots).map(displayValue).filter(Boolean);
  const options = toArray(gap.options).map(displayValue).filter(Boolean);
  const allowedHints = toArray(gap.allowed_hints).map(displayValue).filter(Boolean);
  const forbiddenVisuals = toArray(gap.forbidden_visuals).map(displayValue).filter(Boolean);
  const status = gap.status || 'pending';
  const blocking = Boolean(gap.blocking);

  return (
    <div className="rounded-xl border border-[#ded6c8] bg-white/75 p-3 text-[12px] text-stone-600 dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-gray-300">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-[#d9d0c2] bg-[#f7f2e9] text-[#7d719e] dark:border-white/[0.1] dark:bg-white/[0.06] dark:text-[#b9afe7]">
          {blocking ? <CircleAlert size={14} /> : <ImageIcon size={14} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="truncate font-semibold text-stone-800 dark:text-gray-200">{assetName}</p>
            <span className="shrink-0 rounded-full bg-[#f0ebe2] px-2 py-0.5 text-[10px] text-stone-500 dark:bg-white/[0.06] dark:text-gray-400">{assetType}</span>
            {blocking && <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">阻塞</span>}
          </div>
          <p className="mt-1 text-[11px] text-stone-400">
            状态：{status}
            {linkedShots.length > 0 ? ` · 关联镜头：${linkedShots.slice(0, 3).join('、')}` : ''}
          </p>
        </div>
      </div>

      {(options.length > 0 || allowedHints.length > 0 || forbiddenVisuals.length > 0) && (
        <div className="mt-2 space-y-1 border-t border-[#eee7db] pt-2 text-[11px] leading-5 text-stone-500">
          {options.length > 0 && <p className="line-clamp-2">建议：{options.slice(0, 3).join('、')}</p>}
          {allowedHints.length > 0 && <p className="line-clamp-2">可参考：{allowedHints.slice(0, 3).join('、')}</p>}
          {forbiddenVisuals.length > 0 && <p className="line-clamp-2 text-amber-700">避免：{forbiddenVisuals.slice(0, 3).join('、')}</p>}
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled
          title="等待素材上传接口接入"
          className="rounded-lg border border-[#d9d0c2] bg-[#f4f1ec] px-2 py-2 text-[11px] text-stone-400 opacity-70 dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-gray-500"
        >
          上传 · 待接入
        </button>
        <button
          type="button"
          disabled
          title="等待小影生成缺口素材接口接入"
          className="rounded-lg border border-[#d9d0c2] bg-[#f4f1ec] px-2 py-2 text-[11px] text-stone-400 opacity-70 dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-gray-500"
        >
          让小影生成 · 待接入
        </button>
      </div>
    </div>
  );
}

const GAP_STATES = {
  generating: { label: '生成中…', wrap: 'border-[#bcace0] bg-[#f1ecf9] animate-pulse dark:border-white/[0.14] dark:bg-white/[0.05]', badge: 'bg-[#ece7f1] text-[#675b89] dark:bg-white/[0.1] dark:text-[#b9afe7]', Icon: Loader2, iconCls: 'text-[#7d719e] animate-spin' },
  done: { label: '已可用', wrap: 'border-emerald-200 bg-emerald-50/80 dark:border-emerald-500/30 dark:bg-emerald-500/10', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300', Icon: Check, iconCls: 'text-emerald-600' },
  failed: { label: '生成失败', wrap: 'border-red-200 bg-red-50/80 dark:border-red-500/30 dark:bg-red-500/10', badge: 'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300', Icon: CircleAlert, iconCls: 'text-red-500' },
  pending: { label: '待处理', wrap: 'border-[#ded6c8] bg-white/75 dark:border-white/[0.08] dark:bg-white/[0.04]', badge: 'bg-[#f0ebe2] text-stone-500 dark:bg-white/[0.06] dark:text-gray-400', Icon: ImageIcon, iconCls: 'text-[#7d719e]' },
  missing: { label: '缺失', wrap: 'border-amber-300 bg-amber-50/80 dark:border-amber-500/30 dark:bg-amber-500/10', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300', Icon: CircleAlert, iconCls: 'text-amber-500' },
  skipped: { label: '已跳过', wrap: 'border-stone-200 bg-stone-50/70 opacity-70 dark:border-white/[0.06] dark:bg-white/[0.03]', badge: 'bg-stone-100 text-stone-400 dark:bg-white/[0.05] dark:text-gray-500', Icon: X, iconCls: 'text-stone-400' },
};

function resolveGapState(status, live) {
  if (status === 'skipped') return 'skipped'; // 用户跳过：不做参考图，视频按剧情文字生成
  if (live || ['generating', 'processing'].includes(status)) return 'generating';
  // ready=已就绪 / uploaded=用户上传 / needs_confirm=已生成待确认 / done/usable/accepted… 都算"有图了"
  if (['ready', 'uploaded', 'needs_confirm', 'done', 'usable', 'accepted', 'usable_with_notes', 'succeeded'].includes(status)) return 'done';
  if (['failed', 'retry_recommended', 'rejected'].includes(status)) return 'failed';
  if (status === 'missing') return 'missing';
  return 'pending';
}

// 紧凑一小条：左 复选框/状态图标，中 名称+类型，右 状态字+上传/换
function AssetGapResolveCard({ gap, live, selected, onToggle, onGenerate, onUpload, onReset, onSkip }) {
  const fileRef = useRef(null);
  const assetType = ASSET_TYPE_LABELS[gap.asset_type] || gap.asset_type || '素材';
  const assetName = gap.asset_name || gap.name || gap.label || '素材';
  const stateKey = resolveGapState(gap.status || 'pending', live);
  const state = GAP_STATES[stateKey];
  const generating = stateKey === 'generating';
  const done = stateKey === 'done';
  const failed = stateKey === 'failed';
  const missing = stateKey === 'missing';
  const skipped = stateKey === 'skipped';

  // 档位只用小徽标区分（角色/场景=粉 必备 · 道具/招式=绿 建议），配合上方分组标题；
  // 行本身保持干净中性（沿用状态色 state.wrap），不再整行铺档位色 + 粗色左条（太土）。
  const tierRequired = gap.required !== false;
  const tierBadge = tierRequired ? 'bg-rose-100 text-rose-600' : 'bg-emerald-100 text-emerald-700';
  const wrapCls = state.wrap;

  return (
    <div title={failed && gap.qc_note ? `失败原因：${gap.qc_note}` : undefined}
      className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[12px] transition-all hover:-translate-y-px hover:border-[#c9bedc] hover:shadow-md hover:shadow-stone-300/60 dark:hover:shadow-black/40 ${wrapCls}`}>
      <input ref={fileRef} type="file" accept="image/*" className="hidden"
        onChange={event => { const file = event.target.files?.[0]; event.target.value = ''; if (file) onUpload?.(gap, file); }} />
      {generating ? (
        <Loader2 size={14} className="shrink-0 animate-spin text-[#7d719e]" />
      ) : done ? (
        <Check size={14} className="shrink-0 text-emerald-600" />
      ) : missing ? (
        <CircleAlert size={14} className="shrink-0 text-amber-500" />
      ) : skipped ? (
        <X size={14} className="shrink-0 text-stone-400" />
      ) : (
        <input type="checkbox" checked={Boolean(selected)} onChange={() => onToggle?.(gap)}
          title="勾选 = 自动生成；取消 = 我自己上传或在画布里加" className="h-3.5 w-3.5 shrink-0 cursor-pointer accent-[#7d719e]" />
      )}
      <span className={`truncate font-medium ${skipped ? 'text-stone-400 line-through' : 'text-stone-800 dark:text-gray-200'}`}>{assetName}</span>
      <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] ${tierBadge}`}>{assetType}</span>
      <span className={`ml-auto shrink-0 text-[10px] ${done ? 'text-emerald-600' : failed ? 'text-red-500' : missing ? 'text-amber-600' : generating ? 'text-[#7d719e]' : 'text-stone-400'}`}>{state.label}</span>
      {!generating && !skipped && (
        <button type="button" onClick={() => fileRef.current?.click()} title={missing ? '上传我的图作为该素材' : '上传我的参考图'}
          className="shrink-0 text-stone-400 hover:text-[#6f6196]"><Paperclip size={13} /></button>
      )}
      {generating && onReset && (
        <button type="button" onClick={() => onReset(gap)} title="卡住了？点此停止重置，可重新生成或上传"
          className="shrink-0 text-stone-400 hover:text-red-500"><X size={13} /></button>
      )}
      {(done || failed || missing) && (
        <button type="button" onClick={() => onGenerate?.(gap)} title={done ? '换一张' : missing ? '自动生成这个缺失素材' : '重试生成'}
          className="shrink-0 text-stone-400 hover:text-[#6f6196]"><RefreshCw size={13} /></button>
      )}
      {onSkip && (failed || missing || stateKey === 'pending') && (
        <button type="button" onClick={() => onSkip(gap, true)} title="跳过：不做参考图，视频按剧情文字生成（适合路人、小道具、次要场景）"
          className="shrink-0 text-[10px] text-stone-400 hover:text-stone-600">跳过</button>
      )}
      {onSkip && skipped && (
        <button type="button" onClick={() => onSkip(gap, false)} title="恢复：重新把它当素材"
          className="shrink-0 text-[10px] text-[#7d719e] hover:text-[#6f6196]">恢复</button>
      )}
    </div>
  );
}

function ActionCard({ action, busy, onConfirm, onCancel }) {
  if (!action || action.status === 'cancelled' || action.status === 'completed') return null;
  // pending 首次确认；confirmed 但未执行（上次执行调用失败）允许再点一次重试，避免卡死
  const canConfirm = action.status === 'pending' || action.status === 'confirmed';
  const statusText = actionStatusText(action.status);
  return (
    <div className="border-y border-[#ded6c8] bg-[#f4f1ec] px-4 py-3 dark:border-white/[0.06] dark:bg-white/[0.03]">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 h-7 w-7 shrink-0 rounded-lg border border-[#c9bedc] bg-white flex items-center justify-center text-[#75689a] dark:border-white/[0.12] dark:bg-white/[0.06] dark:text-[#b9afe7]">
          <Sparkles size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-stone-800 dark:text-gray-200">{ACTION_LABELS[action.action_type] || '待确认动作'}</p>
          <p className="mt-1 text-[11px] text-stone-500 dark:text-gray-400">
            {/* 批量动作不显示聚合大额点数（避免舍不得用），只说计费规则；单镜点数在视频节点上看 */}
            {['start_production', 'submit_videos'].includes(action.action_type)
              ? '成功才扣点 · 失败自动退点'
              : (action.estimated_cost > 0 ? `预计 ${action.estimated_cost} 点` : '不扣点')}
            {statusText ? ` · ${statusText}` : ''}
          </p>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={() => onCancel(action)} disabled={busy || !['pending', 'confirmed'].includes(action.status)}
          className="h-8 px-3 rounded-lg border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] text-[12px] text-stone-600 dark:text-gray-300 disabled:opacity-40 flex items-center gap-1">
          <X size={12} />取消
        </button>
        <button onClick={() => onConfirm(action)} disabled={busy || !canConfirm}
          className="ml-auto h-8 px-3 rounded-lg bg-[#7d719e] text-[12px] text-white disabled:opacity-40 flex items-center gap-1.5">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          {action.action_type === 'start_production' ? '建图' : action.action_type === 'submit_videos' ? '生成视频' : action.action_type === 'generate_assets' ? '生成素材' : action.action_type === 'retry_generation' ? '确认方案' : '确认执行'}
        </button>
      </div>
    </div>
  );
}

const normalizeAssetRefs = (value = {}) => {
  const refs = value || {};
  return {
    characters: toArray(refs.characters || refs.character || refs.roles).map(displayValue).filter(Boolean),
    scenes: toArray(refs.scenes || refs.scene || refs.locations || refs.location).map(displayValue).filter(Boolean),
    props: toArray(refs.props || refs.prop || refs.objects).map(displayValue).filter(Boolean),
    fx_move: toArray(refs.fx_move || refs.fx || refs.effects).map(displayValue).filter(Boolean),
  };
};

const joinMeta = (...items) => items.map(displayValue).filter(Boolean).join(' / ');

function CoverageNotice({ coverage, scenes }) {
  const missingFromScenes = scenes.flatMap(scene => toArray(scene.dialogue_coverage?.missing));
  const total = coverage.required_count ?? coverage.dialogue_total ?? coverage.total ?? 0;
  const covered = coverage.covered_count ?? coverage.dialogue_assigned ?? coverage.assigned ?? 0;
  const missingCount = coverage.missing_count ?? missingFromScenes.length ?? Math.max(Number(total) - Number(covered), 0);
  const repaired = Boolean(coverage.repaired || coverage.repair_status === 'repaired');
  const complete = Boolean(coverage.complete || coverage.status === 'complete' || (Number(total) > 0 && Number(covered) >= Number(total) && !missingCount));
  const tone = complete || repaired ? 'border-emerald-100 bg-emerald-50/70 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300' : 'border-[#e4dccf] bg-[#fbf8f1] text-stone-600 dark:border-white/[0.06] dark:bg-white/[0.04] dark:text-gray-300';
  const text = repaired
    ? '已自动补齐台词分配'
    : complete
      ? '台词已全部放进分镜'
      : `还有 ${missingCount || '若干'} 句台词没放进分镜，先补齐后再建图`;

  return (
    <p className={`mt-2 rounded-lg border px-2.5 py-1.5 text-[11px] leading-5 ${tone}`}>
      {text}
    </p>
  );
}

function ShotConfirmCard({ shot, index, sceneId, seqOverride }) {
  const dialogues = toArray(shot.dialogues)
    .map(dialogue => `${dialogue.speaker ? `${dialogue.speaker}: ` : ''}${dialogue.text || ''}${dialogue.delivery ? `（${dialogue.delivery}）` : ''}`)
    .filter(Boolean);
  const audio = toArray(shot.audio || shot.sfx).map(displayValue).filter(Boolean);
  const assets = normalizeAssetRefs(shot.assets_used || shot.asset_refs || shot.assets || {});
  const assetRows = [
    ['人物', assets.characters],
    ['场景', assets.scenes],
    ['道具', assets.props],
    ['FX', assets.fx_move],
  ].filter(([, values]) => values.length);
  // seqOverride：跨场次全局连续编号（片段1..N），与画布上"片段N·视频"节点的编号一致，避免"每场从1重数"造成总数误读
  const seq = Number.isFinite(Number(seqOverride)) ? Number(seqOverride)
    : (Number.isFinite(Number(shot.seq)) ? Number(shot.seq) : index + 1);
  const duration = shot.duration ? `${shot.duration}s` : shot.timeRange || '';
  const frame = shot.shot_size || shot.frame || shot.view || '';
  const camera = joinMeta(shot.camera, shot.camera_move || shot.movement);
  const visual = shot.content || shot.visual || shot.image || shot.title || '待补充分镜画面';

  return (
    <div className="rounded-xl border border-[#e4dccf] bg-white px-3 py-2.5 text-[12px] text-stone-700 dark:border-white/[0.07] dark:bg-white/[0.03] dark:text-gray-300">
      <div className="flex items-center gap-2 text-[11px] text-stone-500 dark:text-gray-400">
        <span className="font-semibold text-[#6f6196] dark:text-[#b9afe7]">片段 {seq}</span>
        {duration && <span>· {duration}</span>}
        {(frame || camera) && <span className="ml-auto truncate">{joinMeta(frame, camera) || '镜头待定'}</span>}
      </div>
      <p className="mt-2 leading-5 text-stone-800 dark:text-gray-200">{visual}</p>

      {(dialogues.length > 0 || audio.length > 0 || assetRows.length > 0) && (
        <details className="mt-2 rounded-lg bg-[#fbf8f1] px-2 py-1.5 dark:bg-white/[0.04]">
          <summary className="cursor-pointer list-none text-[11px] text-stone-400">台词 / 音效 / 资产</summary>
          <div className="mt-1.5 space-y-1.5 text-[11px] leading-5 text-stone-500">
            {dialogues.length > 0 && <p><span className="text-stone-400">台词：</span>{dialogues.join(' / ')}</p>}
            {audio.length > 0 && <p><span className="text-stone-400">音效：</span>{audio.join(' / ')}</p>}
            {assetRows.map(([label, values]) => (
              <p key={`${sceneId}-${seq}-${label}`}><span className="text-stone-400">{label}：</span>{values.join('、')}</p>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function StoryboardConfirmCard({ card }) {
  const scenes = toArray(card?.scenes || card?.storyboard?.scenes);
  const coverage = card?.dialogue_coverage || card?.coverage || {};
  const shotCount = scenes.reduce((sum, scene) => sum + toArray(scene.shots).length, 0);
  const plan = card?.production_plan || card?.start_plan || {};
  const totals = plan.totals || {};
  const assetCount = Number(card?.asset_count) || scenes.reduce((sum, scene) => sum + toArray(scene.assets).length, 0);
  const imageCount = assetCount; // 只算资产参考图（默认不生首帧）
  const videoCount = shotCount;
  const planAspect = toArray(scenes[0]?.shots)[0]?.aspect_ratio || '9:16';
  const hasStartPlan = Boolean(card?.action_id && (totals.totalCost || card?.credits !== undefined || card?.affordable !== undefined));
  // 全局连续编号偏移：场1有N个片段，场2从片段N+1继续数
  let seqCursor = 0;
  const sceneSeqOffsets = scenes.map(scene => { const offset = seqCursor; seqCursor += toArray(scene.shots).length; return offset; });

  return (
    <div className="overflow-hidden rounded-xl border border-[#ded6c8] bg-white/90 text-stone-700 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-gray-300">
      <div className="border-b border-[#eee7db] px-3 py-2.5 dark:border-white/[0.06]">
        <p className="text-[13px] font-semibold text-stone-900 dark:text-gray-100">{hasStartPlan ? '开拍计划' : '剧本拆解确认稿'}</p>
        <p className="mt-1 text-[11px] leading-5 text-stone-500 dark:text-gray-400">
          {card.summary || `已拆成 ${scenes.length} 个场景 / ${shotCount} 个分镜，请核对导演施工稿。`}
        </p>
        {hasStartPlan && (
          <div className="mt-2 rounded-lg border border-[#e4dccf] bg-[#fbf8f1] px-2.5 py-2 text-[11px] leading-5 text-stone-600 dark:border-white/[0.06] dark:bg-white/[0.04] dark:text-gray-300">
            <div className="flex items-center justify-between gap-2">
              <span>预计生成</span>
              <strong className="text-[13px] text-stone-800 dark:text-gray-100">{imageCount} 张图 · {videoCount} 个视频</strong>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5 border-t border-[#eee7db] pt-1.5 text-[10px] text-stone-500 dark:border-white/[0.06] dark:text-gray-400">
              <span className="rounded-md bg-white px-1.5 py-0.5 dark:bg-white/[0.08]">画幅 {planAspect}</span>
              <span className="rounded-md bg-white px-1.5 py-0.5 dark:bg-white/[0.08]">无字幕·无BGM</span>
            </div>
            <p className="mt-1 text-[10px] text-stone-400">成功才扣点 · 失败自动退点</p>
            {card.affordable === false && (
              <p className="mt-1 text-amber-700">余额不足，开拍前需要充值。</p>
            )}
          </div>
        )}
        <CoverageNotice coverage={coverage} scenes={scenes} />
      </div>

      <div className="max-h-[460px] overflow-y-auto">
        {scenes.length === 0 ? (
          <p className="px-3 py-8 text-center text-[12px] text-stone-400">暂无分镜内容。</p>
        ) : scenes.map((scene, sceneIndex) => {
          const shots = toArray(scene.shots);
          const sceneTitle = scene.title || scene.scene_title || scene.location || `场景 ${scene.scene_no || sceneIndex + 1}`;
          return (
            <section key={scene.scene_id || sceneTitle || sceneIndex} className="border-b border-[#eee7db] last:border-b-0 dark:border-white/[0.06]">
              <div className="bg-[#fbf8f1] px-3 py-2 dark:bg-white/[0.04]">
                <div className="flex items-center gap-2 text-[12px]">
                  <span className="min-w-0 flex-1 truncate font-semibold text-stone-800 dark:text-gray-200">{sceneTitle}</span>
                  <span className="shrink-0 text-[11px] text-stone-400">{shots.length} 镜</span>
                </div>
                {(scene.location || scene.time || scene.summary) && (
                  <p className="mt-1 line-clamp-2 text-[11px] leading-5 text-stone-400">
                    {[scene.location, scene.time, scene.summary].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
              <div className="space-y-2 px-3 py-3">
                {shots.map((shot, index) => (
                  <ShotConfirmCard key={shot.shot_id || `${scene.scene_id || sceneIndex}-${index}`} shot={shot} index={index} sceneId={scene.scene_id || sceneIndex} seqOverride={sceneSeqOffsets[sceneIndex] + index + 1} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function MessageItem({ message, action, busy, startSettings, onConfirm, onCancel, onChoice }) {
  const isUser = message.role === 'user';
  const card = message.metadata?.card;
  const choices = !isUser && Array.isArray(message.metadata?.choices) ? message.metadata.choices : null;
  const hidePlainContent = !isUser && card?.type === 'script_plan';
  return (
    <div className={isUser ? 'flex justify-end' : 'space-y-2'}>
      {!hidePlainContent && (
        <div className={`${isUser ? 'max-w-[86%] bg-[#8173a5] text-white' : 'text-stone-700 dark:text-gray-300'} rounded-lg px-3 py-2 text-[13px] leading-6 whitespace-pre-wrap`}>
          {message.content}
        </div>
      )}
      {card?.type === 'script_plan' && <StoryboardConfirmCard card={card} />}
      {card?.type === 'revision_plan' && (
        <div className="border-y border-[#ded6c8] bg-white/70 px-3 py-2 text-[12px] text-stone-600 dark:border-white/[0.06] dark:bg-white/[0.04] dark:text-gray-300">
          仅重做当前节点并保留现有候选：{card.feedback}
        </div>
      )}
      {/* 桌面式可点选项：点一下就走，不用打字 */}
      {choices && (
        <div className="flex flex-wrap gap-2 pt-0.5">
          {choices.map((c, i) => (
            <button key={i} type="button" disabled={busy} onClick={() => onChoice?.(c)}
              className={`rounded-lg border px-3 py-1.5 text-[12px] transition-colors disabled:opacity-40 ${c.primary
                ? 'border-[#c9bedc] bg-[#ece7f1] text-[#675b89] hover:bg-[#e2dbee] dark:bg-white/[0.1] dark:text-[#b9afe7] dark:hover:bg-white/[0.14]'
                : 'border-[#d9d0c2] bg-white text-stone-600 hover:border-[#c9bedc] hover:text-[#6f6196] dark:border-white/[0.1] dark:bg-white/[0.05] dark:text-gray-300 dark:hover:text-[#b9afe7]'}`}>
              {c.label}
            </button>
          ))}
        </div>
      )}
      {action?.action_type === 'start_production' && startSettings}
      {action && <ActionCard action={action} busy={busy} onConfirm={onConfirm} onCancel={onCancel} />}
    </div>
  );
}

// 把选中的普通图片节点设为素材（角色/场景/道具/特效）
function SetAssetBlock({ node, onSet }) {
  const [name, setName] = useState(node.data?.label || '');
  const [type, setType] = useState('character');
  useEffect(() => { setName(node.data?.label || ''); }, [node.id, node.data?.label]);
  return (
    <div className="rounded-xl border border-[#c9bedc] bg-[#f3f0f8] px-3 py-2.5 dark:border-white/[0.12] dark:bg-white/[0.04]">
      <p className="mb-1.5 text-[12px] font-semibold text-[#675b89] dark:text-[#b9afe7]">把选中的图设为素材</p>
      <div className="flex items-center gap-1.5">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="素材名，如 叶长歌"
          className="min-w-0 flex-1 rounded-md border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] px-2 py-1 text-[12px] text-stone-800 dark:text-gray-200 outline-none" />
        <select value={type} onChange={e => setType(e.target.value)}
          className="shrink-0 rounded-md border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] px-1 py-1 text-[11px] text-stone-700 dark:text-gray-200">
          <option value="character">角色</option>
          <option value="scene">场景</option>
          <option value="prop">道具</option>
          <option value="fx_move">特效</option>
        </select>
        <button type="button" onClick={() => onSet?.(node.id, name.trim(), type)} disabled={!name.trim()}
          className="shrink-0 rounded-md bg-[#7d719e] px-2.5 py-1 text-[11px] text-white disabled:opacity-40">设为素材</button>
      </div>
      <p className="mt-1 text-[10px] leading-4 text-stone-400">设为素材后 Agent 才能识别它、连到对应分镜。名字要和剧本里的角色名一致（如叶长歌）。</p>
    </div>
  );
}

const LOADING_STEPS = ['正在分析剧本', '正在拆分场景和分镜', '正在检查台词覆盖', '正在整理素材清单'];
function AgentLoadingBubble() {
  // 一小条，状态文字滚动更新（不是一大块4行）
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep(v => (v + 1) % LOADING_STEPS.length), 1400);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-[#ded6c8] bg-white/80 px-3 py-1.5 text-[12px] text-stone-700">
      <Loader2 size={13} className="animate-spin text-[#7d719e]" />
      <span>{LOADING_STEPS[step]}…</span>
    </div>
  );
}

export default function CanvasAgentPanel({
  agent,
  canvasName,
  selectedNode,
  nodes,
  productionPlan,
  deliveryManifest,
  imageModelLabel,
  videoModelLabel,
  imageModels,
  imageModelValue,
  onImageModelChange,
  artStyles,
  artStyleValue,
  onArtStyleChange,
  videoModels,
  videoModelValue,
  videoResolutions,
  videoResolution,
  videoGenerateAudio,
  videoSupportsGeneratedAudio,
  onVideoModelChange,
  onVideoResolutionChange,
  onVideoGenerateAudioChange,
  notice,
  error,
  onConfirmNodes,
  onPrepareAction,
  onRefreshPlan,
  onRefreshDelivery,
  onArchiveDelivery,
  onLockDelivery,
  onSelectedQc,
  onUploadImageFiles,
  onGenerateGapReference,
  onUploadGapReference,
  onResetGapReference,
  onSkipGapReference,
  onSetNodeAsAsset,
  onGenerateAllAssets,
  generatingGapIds,
}) {
  const [input, setInput] = useState('');
  const [assetListOpen, setAssetListOpen] = useState(false); // 素材清单默认折叠，置顶，不挡对话
  const [referencedNodeIds, setReferencedNodeIds] = useState([]);
  const [mention, setMention] = useState({ open: false, query: '', start: 0, end: 0 });
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const mediaInputRef = useRef(null);
  const actionById = useMemo(() => new Map(agent.actions.map(action => [action.id, action])), [agent.actions]);
  const attachedActionIds = new Set(agent.messages.map(message => message.metadata?.card?.action_id).filter(Boolean));
  const looseActions = agent.actions.filter(action => !attachedActionIds.has(action.id));
  const failedNodes = useMemo(() => nodes.filter(node => node.data?.status === 'failed'
    || ['needs_regen', 'retry_recommended'].includes(node.data?.qc_status)), [nodes]);
  const counts = useMemo(() => ({
    assets: nodes.filter(node => node.data?.agent_role === 'asset').length,
    shots: nodes.filter(node => node.data?.agent_role === 'shot_task').length,
    keyframes: nodes.filter(node => node.data?.agent_role === 'keyframe').length,
    clips: nodes.filter(node => ['clip', 'video_clip'].includes(node.data?.agent_role)).length,
    failed: failedNodes.length,
  }), [nodes, failedNodes]);
  const assetGaps = useMemo(() => {
    const rawGaps = [
      ...toArray(productionPlan?.blocked_gaps),
      ...toArray(productionPlan?.gaps),
    ].filter(gap => gap && typeof gap === 'object');
    const seen = new Set();
    return rawGaps.filter((gap, index) => {
      const key = gap.id || gap.gap_id || `${gap.asset_type || 'asset'}:${gap.asset_name || gap.name || index}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [productionPlan]);
  const blockingGapCount = assetGaps.filter(gap => gap.blocking).length;
  const hasBlockingGaps = blockingGapCount > 0;
  const gapStateOf = (gap) => resolveGapState(gap.status || 'pending', Boolean(gap.gap_id && generatingGapIds?.has(gap.gap_id)));
  // 两档资产（对标 NemoVideo）：必备=角色/场景（跨镜锁一致，进主清单默认勾选自动生成）；
  // 建议=道具/招式特效（视频按剧情文字自动生成，单列、默认不勾，要锁一致才 opt-in 做参考图）
  const requiredGaps = assetGaps.filter(gap => gap.required !== false);
  const suggestedGaps = assetGaps.filter(gap => gap.required === false);
  const readyGapCount = requiredGaps.filter(gap => gapStateOf(gap) === 'done').length;
  const generatingGapCount = assetGaps.filter(gap => gapStateOf(gap) === 'generating').length; // ② 折叠态显示"生成中N"
  // ① 拆解出素材那一刻自动展开清单（让用户逐个选上传/自动生成）；之后用户可手动折叠
  const hasGaps = assetGaps.length > 0;
  useEffect(() => { if (hasGaps) setAssetListOpen(true); }, [hasGaps]);
  const [gapOverrides, setGapOverrides] = useState(() => ({})); // gap_id -> 用户显式勾选覆盖；无覆盖时按 required 默认
  const isGapSelected = (gap) => {
    if (!['pending', 'failed'].includes(gapStateOf(gap))) return false;
    const def = gap.required !== false; // 必备默认勾选、建议默认不勾
    return Object.prototype.hasOwnProperty.call(gapOverrides, gap.gap_id) ? gapOverrides[gap.gap_id] : def;
  };
  const toggleGap = (gap) => setGapOverrides(prev => ({ ...prev, [gap.gap_id]: !isGapSelected(gap) }));
  const selectedGapNodeIds = assetGaps.filter(isGapSelected).map(gap => gap.nodeId).filter(Boolean);
  const renderGapCard = (gap, index) => (
    <AssetGapResolveCard
      key={gap.id || gap.gap_id || `${gap.asset_type}-${gap.asset_name || gap.name || index}`}
      gap={gap}
      live={Boolean(gap.gap_id && generatingGapIds?.has(gap.gap_id))}
      selected={isGapSelected(gap)}
      onToggle={toggleGap}
      onGenerate={onGenerateGapReference}
      onUpload={onUploadGapReference}
      onReset={onResetGapReference}
      onSkip={onSkipGapReference}
    />
  );
  const mentionableNodes = useMemo(() => nodes
    .filter(node => node?.id && (
      node.data?.agent_role === 'asset'
      || node.type === 'imageNode'
      || node.type === 'audioNode'
      || node.type === 'textNode'
      || node.type === 'videoNode'
      || ['clip', 'video_clip'].includes(node.data?.agent_role)
    ))
    .map(node => ({
      id: node.id,
      label: nodeMentionLabel(node),
      type: nodeTypeLabel(node),
      status: node.data?.status || node.data?.qc_status || '',
    }))
    .filter(item => item.label), [nodes]);
  const mentionOptions = useMemo(() => {
    const query = mention.query.trim().toLowerCase();
    const source = query
      ? mentionableNodes.filter(item => `${item.label} ${item.type}`.toLowerCase().includes(query))
      : mentionableNodes;
    return source.slice(0, 8);
  }, [mention.query, mentionableNodes]);

  const updateMentionState = (value, cursor) => {
    const prefix = value.slice(0, cursor);
    const match = /(?:^|\s)@([^\s@]*)$/.exec(prefix);
    if (!match) {
      setMention(current => current.open ? { open: false, query: '', start: cursor, end: cursor } : current);
      return;
    }
    const query = match[1] || '';
    const start = prefix.length - query.length - 1;
    setMention({ open: true, query, start, end: cursor });
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setInput(value);
    updateMentionState(value, event.target.selectionStart || value.length);
  };

  const insertMention = (item) => {
    const before = input.slice(0, mention.start);
    const after = input.slice(mention.end);
    const token = `@${item.label} `;
    const next = `${before}${token}${after}`;
    const cursor = before.length + token.length;
    setInput(next);
    setReferencedNodeIds(current => Array.from(new Set([...current, item.id])));
    setMention({ open: false, query: '', start: cursor, end: cursor });
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(cursor, cursor);
    });
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [agent.messages, agent.actions]);

  const send = async () => {
    const text = input.trim();
    if (!text || agent.loading) return;
    const refs = Array.from(new Set(referencedNodeIds));
    // 发出去立刻清空输入框，不等 agent 处理完（失败再恢复）
    setInput('');
    setReferencedNodeIds([]);
    setMention({ open: false, query: '', start: 0, end: 0 });
    try {
      await agent.sendMessage(text, {
        selectedNodeId: selectedNode?.id || null,
        referencedNodeIds: refs,
        title: canvasName || '剧本拆镜',
      });
    } catch {
      setInput(text);
    }
  };

  // 点击对话里的选项按钮：直接以该选项发起对话，无需打字（桌面式弹选择）
  const handleChoice = (choice) => {
    if (agent.loading || !choice) return;
    if (choice.send) agent.sendMessage(choice.send, { selectedNodeId: selectedNode?.id || null, title: canvasName || '剧本拆镜' }).catch(() => {});
  };

  const handleMediaFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    const imageFiles = files.filter(file => file?.type?.startsWith('image/'));
    const textFiles = files.filter(isTextLikeFile);
    const unsupported = files.filter(file => !file?.type?.startsWith('image/') && !isTextLikeFile(file));
    if (imageFiles.length) onUploadImageFiles?.(imageFiles);
    if (textFiles.length) {
      const texts = await Promise.all(textFiles.map(file => file.text()));
      setInput(current => [current.trim(), texts.map(text => text.trim()).filter(Boolean).join('\n\n')].filter(Boolean).join('\n\n'));
      window.requestAnimationFrame(() => textareaRef.current?.focus());
    }
    if (unsupported.length) {
      agent.setError?.(`暂不支持：${unsupported.map(file => file.name || file.type || '未知文件').join('、')}`);
    }
  };

  // 开拍时把"开拍设置"里现选的模型/分辨率传给后端覆盖默认
  const confirmAction = (action) => {
    if (action?.action_type === 'start_production') {
      return agent.confirmAction(action, { imageModel: imageModelValue, videoModel: videoModelValue, resolution: videoResolution, styleKey: artStyleValue || undefined });
    }
    if (action?.action_type === 'submit_videos') {
      // 聊"生成视频"批量生成：把开拍设置里现选的视频模型/分辨率/音频带过去
      return agent.confirmAction(action, { model: videoModelValue, resolution: videoResolution, generateAudio: videoGenerateAudio });
    }
    if (['generate_assets', 'generate_keyframes'].includes(action?.action_type)) {
      // 聊天卡批量生图：带上现选的图片模型（否则后端吃默认 seedream，用户选的模型被无视）
      return agent.confirmAction(action, {
        model: imageModelValue,
        ...(action.action_type === 'generate_assets' && artStyleValue ? { styleKey: artStyleValue } : {}),
      });
    }
    return agent.confirmAction(action);
  };

  // 开拍计划下方的"开拍设置"：让用户开拍前改图片/视频模型与分辨率
  const startSettingsNode = (
    <div className="border-y border-[#ded6c8] bg-[#f4f1ec] px-4 py-2.5 dark:border-white/[0.06] dark:bg-white/[0.03]">
      <p className="mb-1.5 text-[11px] font-medium text-stone-600 dark:text-gray-300">开拍设置（可改）</p>
      <div className="space-y-1.5">
        <label className="flex items-center gap-2 text-[11px] text-stone-500">
          <span className="w-8 shrink-0">风格</span>
          <select value={artStyleValue || ''} onChange={e => onArtStyleChange?.(e.target.value)}
            className="h-7 flex-1 min-w-0 rounded-lg border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] px-2 text-[11px] text-stone-700 dark:text-gray-200 outline-none">
            <option value="">按题材自动</option>
            {(artStyles || []).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-[11px] text-stone-500">
          <span className="w-8 shrink-0">图片</span>
          <select value={imageModelValue || ''} onChange={e => onImageModelChange?.(e.target.value)}
            className="h-7 flex-1 min-w-0 rounded-lg border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] px-2 text-[11px] text-stone-700 dark:text-gray-200 outline-none">
            {(imageModels || []).map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </label>
        <div className="flex items-center gap-2 text-[11px] text-stone-500">
          <span className="w-8 shrink-0">视频</span>
          <select value={videoModelValue || ''} onChange={e => onVideoModelChange?.(e.target.value)}
            className="h-7 flex-1 min-w-0 rounded-lg border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] px-2 text-[11px] text-stone-700 dark:text-gray-200 outline-none">
            {(videoModels || []).map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
          <select value={videoResolution || ''} onChange={e => onVideoResolutionChange?.(e.target.value)}
            className="h-7 shrink-0 rounded-lg border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] px-2 text-[11px] text-stone-700 dark:text-gray-200 outline-none">
            {(videoResolutions || []).map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>
      {/* 不再显示聚合大额点数（避免用户舍不得用）；点数在每个视频节点上单独显示，成功才扣 */}
      <p className="mt-2 border-t border-[#e4dccf] pt-1.5 text-[10px] text-stone-400">每个镜头的点数在视频节点上单独显示 · 成功才扣点 · 失败自动退点</p>
    </div>
  );

  return (
    <aside className="w-[390px] shrink-0 border-l border-[#ded6c8] bg-[#fbf8f1] dark:bg-[#0c0c10] dark:border-white/[0.06] flex flex-col">
      <div className="h-9 shrink-0 border-b border-[#e4dccf] px-4 text-[12px] font-medium text-stone-500 dark:border-white/[0.06] flex items-center">
        画布 Agent
      </div>

      {selectedNode && selectedNode.type === 'imageNode' && selectedNode.data?.agent_role !== 'asset' && (
        <div className="shrink-0 border-b border-[#e4dccf] px-3 py-2.5">
          <SetAssetBlock node={selectedNode} onSet={onSetNodeAsAsset} />
        </div>
      )}

      <div className="hidden">
        <div className="flex items-center gap-2 text-[12px]">
          <span className="rounded-md bg-[#ece7f1] px-2 py-1 font-medium text-[#6f6196]">{STAGE_LABELS[agent.stage?.stage] || '生产协作'}</span>
          <span className="ml-auto text-stone-400">下一步：{agent.stage?.next || '等待输入'}</span>
        </div>
        {agent.stage?.blocker && <p className="mt-2 text-[11px] text-stone-500">{agent.stage.blocker}</p>}
      </div>

      {assetGaps.length > 0 && (
        <div className="relative z-10 shrink-0 border-b border-[#e4dccf] bg-[#faf9f6] shadow-[0_12px_24px_-16px_rgba(70,55,90,0.35)] dark:border-white/[0.06] dark:bg-white/[0.02] dark:shadow-black/40">
          <button type="button" onClick={() => setAssetListOpen(v => !v)}
            className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-[#f4f1ec] dark:hover:bg-white/[0.04]">
            <span className="text-[12px] font-semibold text-stone-600 dark:text-gray-300">素材清单</span>
            <span className="rounded-full bg-[#f0ebe2] px-2 py-0.5 text-[10px] text-stone-500 dark:bg-white/[0.06] dark:text-gray-400">{readyGapCount}/{requiredGaps.length} 就绪</span>
            {generatingGapCount > 0 && (
              <span className="flex items-center gap-1 rounded-full bg-[#ece7f1] px-2 py-0.5 text-[10px] text-[#675b89] dark:bg-white/[0.1] dark:text-[#b9afe7]">
                <Loader2 size={10} className="animate-spin" />生成中 {generatingGapCount}
              </span>
            )}
            {generatingGapCount === 0 && blockingGapCount > 0 && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">缺 {blockingGapCount}</span>}
            <ChevronDown size={14} className={`ml-auto shrink-0 text-stone-400 transition-transform ${assetListOpen ? 'rotate-180' : ''}`} />
          </button>
          {assetListOpen && (
            <div className="max-h-[42vh] space-y-1.5 overflow-y-auto px-4 pb-3">
              {selectedGapNodeIds.length > 0 && (
                <button onClick={() => onGenerateAllAssets?.(selectedGapNodeIds)} disabled={agent.loading}
                  title="只自动生成勾选的素材；取消勾选的你自己上传或在画布里加"
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#c9bedc] bg-[#ece7f1] px-2.5 py-1 text-[11px] text-[#675b89] disabled:opacity-40 hover:bg-[#e2dbee]">
                  <Sparkles size={12} />生成勾选 {selectedGapNodeIds.length}
                </button>
              )}
              {Array.isArray(artStyles) && artStyles.length > 0 && (
                <div className="flex items-center gap-1.5 text-[11px] text-stone-500 dark:text-gray-400">
                  <span className="shrink-0">美术风格</span>
                  <select value={artStyleValue || ''} onChange={e => onArtStyleChange?.(e.target.value)}
                    title="生成/换一张时套用的题材画风；老画布请手动选对，避免出现代人"
                    className="min-w-0 flex-1 rounded-md border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] px-1.5 py-1 text-[11px] text-stone-700 dark:text-gray-200">
                    <option value="">按题材自动</option>
                    {artStyles.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
              )}
              {requiredGaps.length > 0 && (
                <div className="flex items-center gap-1.5 pt-0.5 text-[10px] font-semibold text-rose-500">
                  <span className="inline-block h-2 w-2 rounded-full bg-rose-400" />必备 · 角色 / 场景
                  <span className="font-normal text-stone-400">跨镜锁一致，建议都生成</span>
                </div>
              )}
              {requiredGaps.map(renderGapCard)}
              {suggestedGaps.length > 0 && (
                <div className="flex items-center gap-1.5 pt-1.5 text-[10px] font-semibold text-emerald-600">
                  <span className="inline-block h-2 w-2 rounded-full bg-emerald-400" />建议 · 道具 / 招式
                  <span className="font-normal text-stone-400">视频按剧情自动生成；要锁一致再勾选做参考图</span>
                </div>
              )}
              {suggestedGaps.map(renderGapCard)}
            </div>
          )}
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {!agent.messages.length && (
          <div className="py-10 text-center text-stone-400">
            <MessageSquare size={24} className="mx-auto mb-2" strokeWidth={1.4} />
            <p className="text-[13px]">把剧本或当前问题发给我</p>
          </div>
        )}
        {agent.messages.map(message => (
          <MessageItem
            key={message.id}
            message={message}
            action={actionById.get(message.metadata?.card?.action_id)}
            busy={agent.loading}
            startSettings={startSettingsNode}
            onConfirm={confirmAction}
            onCancel={agent.cancelAction}
            onChoice={handleChoice}
          />
        ))}
        {agent.loading && <AgentLoadingBubble />}
        {looseActions.map(action => (
          <div key={action.id} className="space-y-2">
            {action.action_type === 'start_production' && startSettingsNode}
            <ActionCard action={action} busy={agent.loading} onConfirm={confirmAction} onCancel={agent.cancelAction} />
          </div>
        ))}

        <details className="hidden">
          <summary className="list-none cursor-pointer flex items-center text-[12px] font-semibold text-stone-600">
            生产详情
            <ChevronDown size={14} className="ml-auto transition-transform group-open:rotate-180" />
          </summary>
          <div className="mt-3 space-y-4">
            <div className="grid grid-cols-4 gap-2 text-center">
              {[['资产', counts.assets], ['分镜', counts.shots], ['首帧', counts.keyframes], ['视频', counts.clips]].map(([label, value]) => (
                <div key={label} className="rounded-lg bg-[#f0ebe2] px-2 py-2">
                  <p className="text-[10px] text-stone-400">{label}</p><p className="text-[13px] font-semibold text-stone-700">{value}</p>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-stone-500 space-y-1">
              <p>图片：{imageModelLabel || '未选择'}</p>
              <p>视频：{videoModelLabel || '未选择'}</p>
              <p>预计点数：{productionPlan?.totals?.totalCost ?? 0} · 余额：{productionPlan?.credits ?? '—'}</p>
              <p className={productionPlan?.dialogue_coverage?.complete === false ? 'text-amber-700' : 'text-emerald-700'}>
                台词覆盖：{productionPlan?.dialogue_coverage?.covered_count ?? 0}/{productionPlan?.dialogue_coverage?.required_count ?? 0}
                {productionPlan?.dialogue_coverage?.complete === false ? ' · 生成已阻止' : ''}
              </p>
            </div>
            <div className="grid grid-cols-[1fr_88px] gap-2">
              <select value={videoModelValue} onChange={event => onVideoModelChange?.(event.target.value)}
                className="h-9 min-w-0 rounded-lg border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] px-2 text-[11px] text-stone-700 dark:text-gray-200 outline-none">
                {(videoModels || []).map(model => <option key={model.key} value={model.key}>{model.label}</option>)}
              </select>
              <select value={videoResolution} onChange={event => onVideoResolutionChange?.(event.target.value)}
                className="h-9 rounded-lg border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] px-2 text-[11px] text-stone-700 dark:text-gray-200 outline-none">
                {(videoResolutions || []).map(value => <option key={value} value={value}>{value}</option>)}
              </select>
            </div>
            {videoSupportsGeneratedAudio && (
              <label className="flex h-9 cursor-pointer items-center gap-2 border-y border-[#e7dfd0] px-1 text-[11px] text-stone-600">
                <input
                  type="checkbox"
                  checked={Boolean(videoGenerateAudio)}
                  onChange={event => onVideoGenerateAudioChange?.(event.target.checked)}
                  className="h-4 w-4 accent-[#7d719e]"
                />
                生成对白与环境声音
              </label>
            )}
            <div className="grid grid-cols-2 gap-2">
              <button onClick={onConfirmNodes} disabled={hasBlockingGaps} className="rounded-lg border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] px-2 py-2 text-[11px] text-stone-600 disabled:opacity-40 flex items-center gap-1.5"><Check size={12} />{hasBlockingGaps ? '先处理缺口' : '确认待生成'}</button>
              <button onClick={onRefreshPlan} className="rounded-lg border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] px-2 py-2 text-[11px] text-stone-600 flex items-center gap-1.5"><RefreshCw size={12} />刷新预检</button>
              <button onClick={() => onPrepareAction('generate_assets')} disabled={hasBlockingGaps || !productionPlan?.counts?.assets} className="rounded-lg border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] px-2 py-2 text-[11px] text-stone-600 disabled:opacity-40 flex items-center gap-1.5"><ImageIcon size={12} />资产 {productionPlan?.totals?.assetCost || 0}点</button>
              <button onClick={() => onPrepareAction('generate_keyframes')} disabled={hasBlockingGaps || !productionPlan?.counts?.keyframes} className="rounded-lg border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] px-2 py-2 text-[11px] text-stone-600 disabled:opacity-40 flex items-center gap-1.5"><Sparkles size={12} />首帧 {productionPlan?.totals?.keyframeCost || 0}点</button>
              <button onClick={() => onPrepareAction('submit_videos')} disabled={hasBlockingGaps || !productionPlan?.counts?.videos} className="col-span-2 rounded-lg border border-[#c9bedc] bg-[#ece7f1] px-2 py-2 text-[11px] text-[#675b89] disabled:opacity-40 flex items-center justify-center gap-1.5"><Video size={12} />视频 {productionPlan?.totals?.videoCost || 0}点</button>
            </div>

            {selectedNode?.data?.agent_role && (
              <div className="border-t border-[#e7dfd0] pt-3">
                <p className="text-[11px] font-semibold text-stone-600 truncate">当前节点：{selectedNode.data.label}</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button onClick={() => onSelectedQc('usable', '人工验收通过')} className="rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-2 text-[11px] text-emerald-700">通过</button>
                  <button onClick={() => onSelectedQc('needs_regen', '人工标记局部返工')} className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-2 text-[11px] text-amber-700">需返工</button>
                </div>
              </div>
            )}

            <div className="border-t border-[#e7dfd0] pt-3 space-y-2">
              <div className="flex items-center text-[11px] text-stone-500">
                <PackageCheck size={13} className="mr-1.5" />可交付 {deliveryManifest?.summary?.deliverable || 0}/{deliveryManifest?.summary?.total || 0}
                {counts.failed > 0 && <span className="ml-auto text-amber-600 flex items-center gap-1"><CircleAlert size={12} />{counts.failed} 项需处理</span>}
              </div>
              {failedNodes.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-[10px] leading-4 text-amber-700">
                  <p className="font-semibold">失败记录 · {failedNodes[0].data?.label || '生产节点'}</p>
                  <p className="mt-1 line-clamp-3">{failedNodes[0].data?.qc_note || '请查看节点并创建局部重试确认卡。'}</p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <button onClick={onRefreshDelivery} className="rounded-lg border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] px-2 py-2 text-[10px] text-stone-600">刷新清单</button>
                <button onClick={onArchiveDelivery} disabled={!deliveryManifest?.summary?.pending_archive} className="rounded-lg border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] px-2 py-2 text-[10px] text-stone-600 disabled:opacity-40">成片入库</button>
                <button onClick={onLockDelivery} disabled={!deliveryManifest?.summary?.archived} className="rounded-lg border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] px-2 py-2 text-[10px] text-stone-600 disabled:opacity-40 flex items-center justify-center gap-1"><Shield size={11} />锁定</button>
              </div>
            </div>
          </div>
        </details>

        {(notice || error || agent.error) && (
          <div className={`rounded-lg border px-3 py-2 text-[12px] leading-5 ${(error || agent.error) ? 'border-red-200 bg-red-50 text-red-600' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
            {error || agent.error || notice}
          </div>
        )}
      </div>

      <div className="p-4 border-t border-[#e4dccf]">
        <div className="rounded-lg border border-[#d9d0c2] bg-white dark:border-white/[0.1] dark:bg-white/[0.05] p-3">
          <input
            ref={mediaInputRef}
            type="file"
            accept="image/*,.txt,.md,.markdown,.csv,.srt,.json,text/*"
            multiple
            className="hidden"
            onChange={handleMediaFiles}
          />
          <div className="relative">
            {mention.open && mentionOptions.length > 0 && (
              <div className="absolute bottom-full left-0 right-0 z-20 mb-2 max-h-52 overflow-y-auto rounded-xl border border-[#ded6c8] bg-white shadow-xl shadow-stone-900/10 dark:border-white/[0.08] dark:bg-[#1a1a22] dark:shadow-black/50">
                {mentionOptions.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    onMouseDown={event => {
                      event.preventDefault();
                      insertMention(item);
                    }}
                    className="flex w-full items-center gap-2 border-b border-[#f0ebe2] px-3 py-2 text-left text-[12px] last:border-b-0 hover:bg-[#f7f2e9] dark:border-white/[0.05] dark:hover:bg-white/[0.05]"
                  >
                    <span className="min-w-0 flex-1 truncate text-stone-700 dark:text-gray-300">@{item.label}</span>
                    <span className="shrink-0 rounded-full bg-[#f0ebe2] px-2 py-0.5 text-[10px] text-stone-500 dark:bg-white/[0.06] dark:text-gray-400">{item.type}</span>
                  </button>
                ))}
              </div>
            )}
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleInputChange}
              onClick={event => updateMentionState(event.currentTarget.value, event.currentTarget.selectionStart || 0)}
              onKeyUp={event => updateMentionState(event.currentTarget.value, event.currentTarget.selectionStart || 0)}
              onKeyDown={event => {
                if (event.key === 'Escape' && mention.open) {
                  setMention({ open: false, query: '', start: 0, end: 0 });
                  return;
                }
                if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                  event.preventDefault();
                  send();
                }
              }}
            rows={3} placeholder="输入剧本、反馈或下一步需求"
            className="w-full resize-none bg-transparent text-[13px] text-stone-800 dark:text-gray-200 placeholder:text-stone-400 outline-none" />
          </div>
          <div className="flex items-center gap-1.5 pt-2">
            <button
              type="button"
              onClick={() => mediaInputRef.current?.click()}
              title="选择图片或文本/剧本文件"
              className="h-8 rounded-lg border border-[#e4dccf] bg-[#f7f2e9] px-2.5 text-[11px] text-stone-500 hover:text-[#6f6196] hover:border-[#c9bedc] flex items-center gap-1.5 dark:border-white/[0.08] dark:bg-white/[0.05] dark:text-gray-400 dark:hover:text-[#b9afe7]"
            >
              <Paperclip size={14} />
              媒体或文件
            </button>
            <span className="min-w-0 truncate text-[10px] text-stone-400">
              {referencedNodeIds.length > 0
                ? `已引用 ${referencedNodeIds.length} 个对象`
                : selectedNode ? `当前：${selectedNode.data?.label || '节点'}` : '输入 @ 可引用画布对象'}
            </span>
            <button onClick={send} disabled={agent.loading || !input.trim()} title="发送"
              className="ml-auto h-9 w-9 rounded-lg bg-[#7d719e] text-white disabled:opacity-40 flex items-center justify-center">
              {agent.loading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}
